/**
 * The small part of Markdown a work instruction actually uses.
 *
 * The procedures were published with headings, numbered steps and bold safety
 * lines, and the screen rendered the source: `# Running the DTF printer` and
 * `**Before you start.**` on screen, hashes and asterisks included. A procedure
 * somebody reads at a machine cannot look like source code.
 *
 * No library, because the design brief says no new dependencies, and because a
 * general Markdown parser is a large surface for six constructs. What is
 * supported is what these documents contain and nothing else:
 *
 *   # ## ###   headings
 *   1. 2. 3.   ordered steps
 *   - or *     unordered points
 *   **bold**   emphasis inside a paragraph
 *   [a](/b)    a link, to a same-site path or an http address
 *   `code`     a literal, for a code or a temperature
 *   blank line paragraph break
 *
 * Anything else is left as the text it is, which is the honest failure for a
 * renderer: show the words, never swallow them.
 *
 * SAFETY. Every character is escaped BEFORE any markup is applied, so a
 * document body cannot inject an element. That matters here more than in most
 * places: a document is written by one member of staff and read by another, so
 * its body is untrusted input to everybody except its author. R-194.
 */

export interface Block {
  kind: 'h1' | 'h2' | 'h3' | 'p' | 'ol' | 'ul';
  /** Inline HTML, already escaped. One string for a heading or paragraph. */
  html?: string;
  /** Inline HTML per item, already escaped. */
  items?: string[];
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/**
 * Bold, code and links, on text that is ALREADY escaped.
 *
 * Links were added for the help manual, where a page that cannot point at the
 * next page is a page somebody leaves. Procedures render through the same
 * function and gain them too, which is a small bonus rather than the reason.
 *
 * **The href is checked, not trusted.** The body of a document is written by
 * one member of staff and read by another, so it is untrusted input to
 * everybody except its author. `javascript:` in an href is a script that runs
 * when a colleague clicks a manual, and escaping the text does nothing about
 * it. Only a same-site path or an http address is allowed through; anything
 * else renders as plain text, which is the honest failure for a renderer:
 * show the words, never swallow them.
 */
function safeHref(raw: string): string | null {
  const href = raw.trim();
  if (href.startsWith('/') && !href.startsWith('//')) return href;
  if (/^https?:\/\//i.test(href)) return href;
  if (/^#[\w-]+$/.test(href)) return href;
  return null;
}

function inline(text: string): string {
  return escapeHtml(text)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="rounded-[4px] bg-raised px-1 py-0.5 font-[family-name:var(--font-mono)] text-[0.92em]">$1</code>')
    // `[text](href)`. The text is already escaped; the href is checked.
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (whole, label: string, href: string) => {
      const safe = safeHref(href);
      if (!safe) return whole;
      const external = /^https?:\/\//i.test(safe);
      const attrs = external ? ' target="_blank" rel="noreferrer noopener"' : '';
      return `<a href="${safe}" class="underline underline-offset-2 decoration-line-strong hover:decoration-ink"${attrs}>${label}</a>`;
    });
}

/**
 * A document body, as blocks.
 *
 * Returned as data rather than one HTML string so a caller can decide the
 * spacing, and so this can be tested without a DOM.
 */
export function parseMarkdown(source: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { kind: 'ol' | 'ul'; items: string[] } | null = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push({ kind: 'p', html: inline(paragraph.join(' ')) });
    paragraph = [];
  };
  const flushList = () => {
    if (!list) return;
    blocks.push({ kind: list.kind, items: list.items });
    list = null;
  };
  const flush = () => { flushParagraph(); flushList(); };

  for (const raw of source.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trimEnd();

    if (!line.trim()) { flush(); continue; }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      const level = heading[1]!.length;
      blocks.push({ kind: `h${level}` as 'h1' | 'h2' | 'h3', html: inline(heading[2]!) });
      continue;
    }

    const ordered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (ordered) {
      flushParagraph();
      if (list?.kind !== 'ol') { flushList(); list = { kind: 'ol', items: [] }; }
      list.items.push(inline(ordered[1]!));
      continue;
    }

    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      flushParagraph();
      if (list?.kind !== 'ul') { flushList(); list = { kind: 'ul', items: [] }; }
      list.items.push(inline(bullet[1]!));
      continue;
    }

    // A line that continues the one above it. A step wrapped across two lines
    // in the source is one step on screen.
    if (list) { list.items[list.items.length - 1] += ` ${inline(line.trim())}`; continue; }
    paragraph.push(line.trim());
  }

  flush();
  return blocks;
}
